import { Component, OnInit, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DepartmentService } from 'src/app/services/department.service';
import { Template, TemplateItem } from '../template.model';
import { TemplateService } from '../template.service';
import validator from 'validator';
import { CommonService } from 'src/app/services/common.service';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TemplateInfoComponent } from '../template-info/template-info.component';
import { FormsModule } from '@angular/forms';
import { ErrorService } from '../../../common/errors/error.service';
import { HasUnsavedChanges } from 'src/app/guards/unsaved-changes.guard';


@Component({
    selector: 'app-template-detail',
    templateUrl: './template-detail.component.html',
    styleUrl: './template-detail.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, NgbTooltip]
})
export class TemplateDetailComponent implements OnInit, HasUnsavedChanges {

  template: Template = {} as Template;
  errormessageForTemplatename: string = '';
  readableSystemFields: TemplateItem[] = [];

  isLoading: boolean = true;
  templateWorkingText: string = '';

  // Gemte feltnavne (id → navn), så omdøbning af nøglefelter kan opdages ved gem.
  private savedFieldNames = new Map<string, string>();
  // Når der er forsøgt gemt én gang, valideres der igen ved hver ændring, så fejl forsvinder, når de er rettet.
  private hasValidated: boolean = false;
  // Skabelonen som den sidst blev hentet/gemt, så ugemte ændringer kan opdages.
  private savedState = '';
  // Sat når skabelonen er slettet: der er intet at gemme, så navigationen væk må ikke advare.
  private leaveWithoutCheck = false;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastrService: ToastrService,
    private departmentService: DepartmentService,
    private templateService: TemplateService,
    private commonService: CommonService,
    private ngmService: NgbModal,
    private errorService: ErrorService

  ) {
  }


  async ngOnInit(): Promise<void> {
    const _templateId = this.route.snapshot.paramMap.get('templateid');
    if (_templateId == null || ! validator.isUUID(_templateId)) {
        this.errorService.notify('TemplateId ['+ this.route.snapshot.paramMap.get('templateid') + '] er ikke gyldigt!');
        this.router.navigate(['/home']);
    }
    else
    {
       await this.templateService.getTemplate(_templateId).then((template: Template) => {
          this.template = template; 
          // Linket bestemmer afdelingen: skift til skabelonens afdeling i stedet for at bruge den valgte.
          if (this.departmentService.getDepartment().id !== template.departmentId) {
            this.departmentService.setSelectedDepartment({ id: template.departmentId, code: template.departmentCode, name: template.departmentName });
          }
          this.template.templatecrud = '';
          this.errormessageForTemplatename = '';
          this.readableSystemFields = [];
          this.rememberSavedFieldNames();
          if (this.template.items != null && this.template.items.length > 0) {
            this.readableSystemFields = this.template.items.filter(x => x.dbTableitemInfo.isSystemfield && x.dbTableitemInfo.isReadable); 
          }

          if (template.dbAllowRead) { 
            this.template.templatecrud += 'Læse';
          }

          if (template.dbAllowWrite) 
          { 
            if (this.template.templatecrud.length > 0) {
              this.template.templatecrud += ', skrive';
            }
            else
            {
              this.template.templatecrud += 'Skrive';
            }
          }

          if (template.dbAllowDelete) 
          { 
            if (this.template.templatecrud.length > 0) {
              this.template.templatecrud += ', slette';
            }
            else
            {
              this.template.templatecrud += 'Slet';
            }
          }
  
          this.template.items.forEach(element => {

                    console.log('Validerer element: ' + element.name + ' - ' + element.dbTableitemInfo.defaultValueTypeNonSql);

            element.errormessage = '';
            element.errormessageForFieldname = '';
            element.showDbInfo = true;

            element.itemcrud = '';
            if (element.dbTableitemInfo.isCreateable) 
            { 
              element.itemcrud += 'Opret';
            }
            
            if (element.dbTableitemInfo.isUpdateable) 
              { 
                if (element.itemcrud.length > 0) {
                  element.itemcrud += ', opdater';
                }
                else
                {
                  element.itemcrud += 'Opdater';
                }
              }
  
            if (element.dbTableitemInfo.isReadable) { 
              if (element.itemcrud.length > 0) {
                element.itemcrud += ', læs';
              }
              else
              {
                element.itemcrud += 'Læs';
              }
            }

          });
          this.rememberSavedState();
       }).catch(() => {
          // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
       }).finally(() => {
        this.isLoading = false;
       });
    }
  }

  onDefaultOptionChange($event: Event,  item: TemplateItem) {
    item.mandatory = ($event.target as HTMLSelectElement).value === '1';
    this.revalidate();
  }

  onDefaultOption(item: TemplateItem, value: string) {
    item.defaultValue = value;
    this.revalidate();
  }

  anyErrors(): boolean {
    return this.template.items.some(item => item.errormessage.length > 0 || item.errormessageForFieldname.length > 0);
  }

  // Kaldes ved ændringer i felttabellen; validerer kun igen, når der allerede er forsøgt gemt.
  revalidate() {
    if (this.hasValidated) {
      this.validateItems();
    }
  }

  private rememberSavedFieldNames() {
    this.savedFieldNames = new Map((this.template.items ?? []).map(item => [item.id, item.name]));
  }

  private rememberSavedState() {
    this.savedState = this.currentState();
  }

  // Valideringens fejlbeskeder og fold ud/ind af databaseinfo er ikke ændringer af skabelonen.
  private currentState(): string {
    const ignored = new Set(['errormessage', 'errormessageForFieldname', 'showDbInfo']);
    return JSON.stringify(this.template, (key, value) => ignored.has(key) ? undefined : value);
  }

  /** Ændringer på siden, der ikke er gemt — vist som en rød prik på "Opdater". */
  isDirty(): boolean {
    return !this.isLoading && this.savedState !== '' && this.currentState() !== this.savedState;
  }

  // Kaldes af unsavedChangesGuard ved al navigation væk fra siden (tilbage-knappen, menuen, browserens tilbage).
  async confirmLeave(): Promise<boolean> {
    if (this.leaveWithoutCheck || !this.isDirty()) {
      return true;
    }
    return await this.commonService.confirm('Ugemte ændringer', 'Der er ændringer til skabelonen, som ikke er gemt. Vil du forlade siden uden at gemme?', 'Ja', 'Nej', 'lg').catch(() => false);
  }

  // Genindlæsning eller lukning af fanen går uden om routeren; browseren viser sin egen advarsel.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (!this.leaveWithoutCheck && this.isDirty()) {
      event.preventDefault();
    }
  }

  onGoBack() {
    this.router.navigate(['/templates']);
  }

  async onUpdateTemplate()
  {
      this.errormessageForTemplatename = '';

      if (this.template.name == null || this.template.name == '' || this.template.name.trim().length == 0)
      {
        this.errorService.notify('Mangler navn');
        this.errormessageForTemplatename = 'Mangler navn';
        return;
      }

      this.hasValidated = true;
      const anyErrors = this.validateItems();
      if (anyErrors > 0)
      {
        this.errorService.notify('Kunne ikke gemmes! Der er '+anyErrors+' fejl');
        return;
      }

      // Nøglefeltets navn er det, Business Central sender; en omdøbning kræver, at BC sender det nye navn.
      const renamedKeyField = this.template.items.some(item =>
        item.dbTableitemInfo.isKey && this.savedFieldNames.has(item.id) && this.savedFieldNames.get(item.id) !== item.name);
      if (renamedKeyField)
      {
        const confirmed = await this.commonService.confirm('Advarsel', 'Du er ved at omdøbe et nøglefelt. Fortsæt?', 'Ja', 'Nej', 'lg').catch(() => false);
        if (!confirmed)
        {
          return;
        }
      }

      this.templateWorkingText = 'Opdater skabelon..';
      this.templateService.updateTemplate(this.template).then(x => {
        this.rememberSavedFieldNames();
        this.rememberSavedState();
        this.toastrService.success('Skabelonen er opdateret');
      })
      .catch(() => {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      })
      .finally(() => {
        this.templateWorkingText = '';
      });
    }

  // Validerer felterne, sætter fejlbeskederne og returnerer antallet af fejl.
  private validateItems(): number
  {
      let anyErrors: number = 0;

      // Låste felter (databaseskabelonen tillader ikke "Rediger") kan ikke ændres, så de valideres ikke.
      if (! this.template.canEditFields)
      {
        return anyErrors;
      }

      // valider template items..
      this.template.items.forEach(element => {
        element.errormessage = '';
        element.errormessageForFieldname = '';

        // Er det et ikke systemfelt?
        if (! element.dbTableitemInfo.isSystemfield)
        {
          if (element.name == null || element.name == '' )
          {
            element.errormessageForFieldname = 'Mangler navn';
            anyErrors++;
          }

          // Feltet skal udfyldes.. så default values er ikke nødvendige!
          if (element.mandatory)
          {
              return;
          }

          if (element.defaultValue == null || element.defaultValue == '' && ! element.dbTableitemInfo.isNullable)
          {
            element.errormessage = 'Mangler defaultværdi';
            anyErrors++;
          } 
          else        
          {
            if (element.defaultValue == 'System: Null' && element.dbTableitemInfo.isNullable) 
            {
              return;
            }
  
            switch (element.dbTableitemInfo.dataType.toLowerCase()) {
              case 'nvarchar':
              case "varchar":
                if (element.defaultValue.length == 0 && element.dbTableitemInfo.isNullable == false)
                {
                  element.errormessage = 'Mangler defaultværdi';
                  anyErrors++;
                }
                break;
              case 'int':                
                if ( element.defaultValue == 'System: Null')
                  {
                      break;
                  }
    
                if (! validator.isInt(element.defaultValue))
                {
                  element.errormessage = 'Skal være et heltal';
                  anyErrors++;
                }
                break;
              case 'uniqueidentifier':
                if (element.defaultValue == 'System: Ny Guid' || element.defaultValue == 'System: Ny UUID' || element.defaultValue == 'System: Tom Guid' || element.defaultValue == 'System: Null')
                  {
                    break;
                  }
                if (! validator.isUUID(element.defaultValue))
                {
                  element.errormessage = 'Skal af typen GUID';
                  anyErrors++;
  
                }
                break;
              case 'datetime':
              case 'datetime2':
                if (element.defaultValue == 'System: Nu' || element.defaultValue == 'System: Null')
                {
                    break;
                }
                if (! validator.isISO8601(element.defaultValue))
                {
                  element.errormessage = 'Skal være en dato';
                  anyErrors++;
  
                }
                break;
              case 'bit':
                if (! validator.isBoolean(element.defaultValue) && element.dbTableitemInfo.isNullable == false)
                {
                  element.errormessage = 'Skal være en boolean (må ikke være null)';
                  anyErrors++;
  
                }
                break;
              case 'float':
                if (! validator.isFloat(element.defaultValue))
                {
                  element.errormessage = 'Skal være et decimaltal';
                  anyErrors++;
                }
                break;
              case 'decimal':
                  if (! validator.isDecimal(element.defaultValue))
                  {
                    element.errormessage = 'Skal være et decimaltal';
                    anyErrors++;
                  }
                break;
              default:
                break;
            }
          }
        }
      });

      // Feltnavnet er JSON-nøglen mod Business Central, så det skal være unikt (uden hensyn til store/små bogstaver).
      const nameCounts = new Map<string, number>();
      this.template.items.forEach(element => {
        const key = (element.name ?? '').trim().toLowerCase();
        if (key.length > 0) {
          nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
        }
      });
      this.template.items.forEach(element => {
        const key = (element.name ?? '').trim().toLowerCase();
        if (! element.dbTableitemInfo.isSystemfield && element.errormessageForFieldname == '' && (nameCounts.get(key) ?? 0) > 1)
        {
          element.errormessageForFieldname = 'Feltnavnet bruges af et andet felt i skabelonen';
          anyErrors++;
        }
      });

      return anyErrors;
    }
    
    public onDisable()
    {
      if (this.template.enabled)
        {
          this.commonService.confirm('Deaktiver skabelon', 'Skabelonen vil ikke være synligt for synkroniseringer! Vil du deaktivere skabelonen?', 'Ja', 'Nej','lg')
          .then((confirmed) => { 
            if (confirmed) {
              this.template.enabled = false;
            }
            else {this.template.enabled = true;}
           })
          .catch(() => {this.template.enabled = true;});  
        }
    }

    public onDeletetemplate()
    {
      this.commonService.confirm('Slet skabelon', 'Skabelonen skal oprettes på ny! Vil du slette skabelonen?', 'Ja', 'Nej','lg')
      .then((confirmed) => { 
        if (confirmed) {
          this.templateWorkingText = 'Sletter skabelon..';
          this.templateService.deleteTemplate(this.template.id).then(x => {         
            this.toastrService.success('Skabelonen er slettet');
            this.leaveWithoutCheck = true;
            this.router.navigate(['/templates']);
          })
          .catch(() => {
            // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
          })
          .finally(() => {
            this.templateWorkingText = '';
          });
        }
       })
    }

      onShowtemplateinfo() {
        const modalRef = this.ngmService.open(TemplateInfoComponent, { size: 'lg', backdrop: 'static' });
        modalRef.componentInstance.template = this.template;
        modalRef.result.then(async x => { }, y => { });
      }      
}
